/**
 * Legitimation Constants - Art. 96 LOPDGDD (Post-Mortem)
 * Frontend shared constants for legitimation-related features
 *
 * Legal basis: LOPDGDD Article 96 - Right to be forgotten for deceased persons
 *
 * NOTE: Keep in sync with server/config/legitimation.constants.js
 *
 * @module constants/legitimation
 */

/**
 * Legitimation types for post-mortem cases
 * Based on Article 96 LOPDGDD - Right to be forgotten for deceased persons
 */
export const LEGITIMATION_TYPES = {
  HEREDERO: 'heredero',
  ALBACEA: 'albacea',
  FAMILIAR_ALLEGADO: 'familiar_allegado',
  MANDATARIO: 'mandatario'
};

/**
 * Array of valid legitimation type values (for validators)
 */
export const LEGITIMATION_TYPE_VALUES = Object.values(LEGITIMATION_TYPES);

/**
 * Display labels for legitimation types (Spanish)
 */
export const LEGITIMATION_LABELS = {
  heredero: 'Heredero/a',
  albacea: 'Albacea / Albacea digital',
  familiar_allegado: 'Familiar o persona allegada',
  mandatario: 'Mandatario/a designado por los herederos'
};

/**
 * Document type labels for legitimation (Spanish)
 */
export const LEGITIMATION_DOCUMENT_LABELS = {
  death_certificate: 'Certificado literal de defuncion',
  applicant_id: 'DNI/NIE/Pasaporte del solicitante',
  inheritance_acceptance: 'Escritura de aceptacion de herencia',
  will_certificate: 'Testamento + Certificado de ultimas voluntades',
  heir_declaration: 'Acta notarial de declaracion de herederos',
  will_with_designation: 'Testamento con designacion de albacea',
  family_book: 'Libro de familia',
  marriage_certificate: 'Certificado de matrimonio',
  birth_certificate: 'Certificado de nacimiento',
  partnership_certificate: 'Certificado de pareja de hecho',
  cohabitation_certificate: 'Certificado de convivencia',
  heir_mandate: 'Mandato o autorizacion de los herederos'
};

/**
 * Required documentation per legitimation type
 * Synchronized with server/config/legitimation.constants.js
 */
export const LEGITIMATION_DOCUMENTATION = {
  heredero: {
    required: ['death_certificate', 'applicant_id'],
    legitimation: ['inheritance_acceptance', 'will_certificate', 'heir_declaration'],
    conditional: ['heir_mandate'],
    description: 'Persona designada por testamento o ley para recibir bienes del fallecido'
  },
  albacea: {
    required: ['death_certificate', 'applicant_id'],
    legitimation: ['will_with_designation'],
    conditional: [],
    description: 'Persona designada por el testador para ejecutar su voluntad testamentaria'
  },
  familiar_allegado: {
    required: ['death_certificate', 'applicant_id'],
    legitimation: ['family_book', 'marriage_certificate', 'birth_certificate', 'partnership_certificate', 'cohabitation_certificate'],
    conditional: [],
    description: 'Persona vinculada al fallecido por razones familiares o de hecho'
  },
  mandatario: {
    required: ['death_certificate', 'applicant_id', 'heir_mandate'],
    legitimation: ['inheritance_acceptance', 'will_certificate', 'heir_declaration'],
    conditional: [],
    description: 'Persona autorizada por los herederos para actuar en su nombre'
  }
};

/**
 * Check if a legitimation type is valid
 * @param {string} type - Legitimation type to validate
 * @returns {boolean} True if valid
 */
export const isValidLegitimationType = (type) =>
  LEGITIMATION_TYPE_VALUES.includes(type);

/**
 * Subtypes for familiar_allegado legal capacity (GAP-05)
 * Each subtype has specific documentation requirements per LOPDGDD Art. 96
 * Synchronized with server/config/legitimation.constants.js
 */
export const FAMILIAR_SUBTYPES = {
  conyuge: {
    label: 'Conyuge',
    documents: ['marriage_certificate']
  },
  pareja_hecho: {
    label: 'Pareja de hecho',
    documents: ['partnership_certificate']
  },
  conviviente: {
    label: 'Conviviente',
    documents: ['cohabitation_certificate']
  },
  hijo: {
    label: 'Hijo/a',
    documents: ['family_book', 'birth_certificate']
  },
  padre: {
    label: 'Padre/Madre',
    documents: ['family_book', 'birth_certificate']
  },
  hermano: {
    label: 'Hermano/a',
    documents: ['family_book']
  },
  otro_familiar: {
    label: 'Otro familiar',
    documents: ['family_book', 'birth_certificate']
  }
};

/**
 * Array of valid familiar subtype values (for validators)
 */
export const FAMILIAR_SUBTYPE_VALUES = Object.keys(FAMILIAR_SUBTYPES);

/**
 * Get specific documentation for a familiar subtype
 * @param {string} subtype - The familiar subtype
 * @returns {string[]} Array of document keys required for this subtype
 */
export const getFamiliarSubtypeDocuments = (subtype) => {
  return FAMILIAR_SUBTYPES[subtype]?.documents || [];
};

/**
 * Valid relationship types for each legal capacity (GAP-04)
 * Used for cross-validation between relationship and legalCapacity
 * Synchronized with server/config/legitimation.constants.js
 */
export const VALID_RELATIONSHIP_FOR_CAPACITY = {
  heredero: ['child', 'parent', 'sibling', 'spouse', 'legal_representative'],
  albacea: ['spouse', 'child', 'parent', 'sibling', 'legal_representative', 'other'],
  familiar_allegado: ['spouse', 'child', 'parent', 'sibling', 'other'],
  mandatario: ['spouse', 'child', 'parent', 'sibling', 'legal_representative', 'other']
};

/**
 * Warning messages for incoherent relationship/capacity combinations
 */
export const RELATIONSHIP_CAPACITY_WARNINGS = {
  heredero: 'La condicion de heredero/a normalmente corresponde a familiares directos (hijos, padres, hermanos o conyuge).',
  familiar_allegado: 'La condicion de familiar allegado requiere un vinculo familiar o de convivencia con el fallecido.'
};

/**
 * Validate coherence between relationship and legalCapacity
 * @param {string} relationship - The relationship with deceased
 * @param {string} legalCapacity - The legal capacity type
 * @returns {{ isValid: boolean, warning: string|null }}
 */
export const validateRelationshipCapacityCoherence = (relationship, legalCapacity) => {
  if (!relationship || !legalCapacity) {
    return { isValid: true, warning: null };
  }

  const validRelationships = VALID_RELATIONSHIP_FOR_CAPACITY[legalCapacity];
  if (!validRelationships) {
    return { isValid: true, warning: null };
  }

  const isValid = validRelationships.includes(relationship);
  if (!isValid) {
    return {
      isValid: false,
      warning: RELATIONSHIP_CAPACITY_WARNINGS[legalCapacity] ||
        'La combinacion de relacion y condicion legal seleccionada puede requerir documentacion adicional.'
    };
  }

  return { isValid: true, warning: null };
};

export default {
  LEGITIMATION_TYPES,
  LEGITIMATION_TYPE_VALUES,
  LEGITIMATION_LABELS,
  LEGITIMATION_DOCUMENT_LABELS,
  LEGITIMATION_DOCUMENTATION,
  FAMILIAR_SUBTYPES,
  FAMILIAR_SUBTYPE_VALUES,
  getFamiliarSubtypeDocuments,
  VALID_RELATIONSHIP_FOR_CAPACITY,
  RELATIONSHIP_CAPACITY_WARNINGS,
  validateRelationshipCapacityCoherence,
  isValidLegitimationType
};
